import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ApiResponse } from '../_model/api-response';
import {
  AdditionalDocumentDownloadUrlDto,
  AdditionalDocumentRequestDetailsDto,
  AdditionalDocumentRequestListItemDto,
  AdditionalDocumentRequestQuery,
  AdditionalDocumentReviewResultDto,
  AdditionalDocumentUploadUrlResponseDto,
  CreateAdditionalDocumentRequestDto
} from '../_model/additional-documents';

@Injectable({ providedIn: 'root' })
export class AdditionalDocumentsService {
  private readonly base = environment.apiBaseUrl;

  constructor(private http: HttpClient) {}

  getMine(status: string | null, page = 1, size = 20): Observable<ApiResponse<AdditionalDocumentRequestListItemDto[]>> {
    let params = new HttpParams()
      .set('page', page)
      .set('size', size);

    if (status) params = params.set('status', status);

    return this.http.get<ApiResponse<AdditionalDocumentRequestListItemDto[]>>(
      `${this.base}/additional-documents/mine`,
      { params }
    );
  }

  getMineById(requestId: string): Observable<ApiResponse<AdditionalDocumentRequestDetailsDto>> {
    return this.http.get<ApiResponse<AdditionalDocumentRequestDetailsDto>>(
      `${this.base}/additional-documents/${requestId}`
    );
  }

  createUploadUrl(requestId: string, file: File): Observable<ApiResponse<AdditionalDocumentUploadUrlResponseDto>> {
    return this.http.post<ApiResponse<AdditionalDocumentUploadUrlResponseDto>>(
      `${this.base}/additional-documents/${requestId}/uploads/presign`,
      {
        fileName: file.name,
        mimeType: file.type || 'application/octet-stream'
      }
    );
  }

  uploadFile(uploadUrl: string, file: File): Observable<string> {
    return this.http.put(uploadUrl, file, {
      headers: new HttpHeaders({
        'Content-Type': file.type || 'application/octet-stream'
      }),
      responseType: 'text'
    });
  }

  confirmUpload(
    requestId: string,
    submissionId: string,
    file: File
  ): Observable<ApiResponse<AdditionalDocumentRequestDetailsDto>> {
    return this.http.post<ApiResponse<AdditionalDocumentRequestDetailsDto>>(
      `${this.base}/additional-documents/${requestId}/uploads/confirm`,
      {
        submissionId,
        fileSizeBytes: file.size,
        metadataJson: JSON.stringify({
          lastModified: file.lastModified,
          browserMimeType: file.type || null
        })
      }
    );
  }

  getUserDownloadUrl(
    requestId: string,
    submissionId: string
  ): Observable<ApiResponse<AdditionalDocumentDownloadUrlDto>> {
    return this.http.get<ApiResponse<AdditionalDocumentDownloadUrlDto>>(
      `${this.base}/additional-documents/${requestId}/submissions/${submissionId}/download-url`
    );
  }

  getAdminRequests(query: AdditionalDocumentRequestQuery): Observable<ApiResponse<AdditionalDocumentRequestListItemDto[]>> {
    let params = new HttpParams()
      .set('page', query.page ?? 1)
      .set('rowsPerPage', query.rowsPerPage ?? 20);

    if (query.status) params = params.set('status', query.status);
    if (query.scopeType) params = params.set('scopeType', query.scopeType);
    if (query.userId) params = params.set('userId', query.userId);
    if (query.depositId) params = params.set('depositId', query.depositId);
    if (query.requestTypeCode) params = params.set('requestTypeCode', query.requestTypeCode);

    return this.http.get<ApiResponse<AdditionalDocumentRequestListItemDto[]>>(
      `${this.base}/admin/additional-documents`,
      { params }
    );
  }

  getAdminRequest(requestId: string): Observable<ApiResponse<AdditionalDocumentRequestDetailsDto>> {
    return this.http.get<ApiResponse<AdditionalDocumentRequestDetailsDto>>(
      `${this.base}/admin/additional-documents/${requestId}`
    );
  }

  createRequest(payload: CreateAdditionalDocumentRequestDto): Observable<ApiResponse<AdditionalDocumentRequestDetailsDto>> {
    return this.http.post<ApiResponse<AdditionalDocumentRequestDetailsDto>>(
      `${this.base}/admin/additional-documents`,
      payload
    );
  }

  startReview(requestId: string, notes: string | null): Observable<ApiResponse<AdditionalDocumentReviewResultDto>> {
    return this.http.post<ApiResponse<AdditionalDocumentReviewResultDto>>(
      `${this.base}/admin/additional-documents/${requestId}/start-review`,
      { notes }
    );
  }

  approve(requestId: string, notes: string | null): Observable<ApiResponse<AdditionalDocumentReviewResultDto>> {
    return this.http.post<ApiResponse<AdditionalDocumentReviewResultDto>>(
      `${this.base}/admin/additional-documents/${requestId}/approve`,
      { notes }
    );
  }

  reject(requestId: string, reason: string): Observable<ApiResponse<AdditionalDocumentReviewResultDto>> {
    return this.http.post<ApiResponse<AdditionalDocumentReviewResultDto>>(
      `${this.base}/admin/additional-documents/${requestId}/reject`,
      { reason }
    );
  }

  getAdminDownloadUrl(
    requestId: string,
    submissionId: string
  ): Observable<ApiResponse<AdditionalDocumentDownloadUrlDto>> {
    return this.http.get<ApiResponse<AdditionalDocumentDownloadUrlDto>>(
      `${this.base}/admin/additional-documents/${requestId}/submissions/${submissionId}/download-url`
    );
  }
}
